<template>
  <div id="swoop-root">
    <div class="root-content">
      <router-view name="sidebar" ref="sidebar" :mobileSidebarOpen="mobileSidebarOpen" @close-mobile-sidebar="closeMobileSidebar" />
      <div class="main-area">
        <router-view class="header" name="header" :mobileSidebarOpen="mobileSidebarOpen" @toggle-mobile-sidebar="toggleMobileSidebar" @close-mobile-sidebar="closeMobileSidebar" />
        <main class="content">
          <router-view />
        </main>
        <router-view name="footer" />
      </div>
    </div>
    <portal-target name="tooltip"></portal-target>
    <notifications :duration="10000" />
    <notifications group="auth" :duration="7000" :max="1" />
    <RootModals />
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters, mapActions } from 'vuex'
import { authGetters, logoutRoutine } from '@/store/modules/auth/routines'
import { notificationGetters } from '@/store/modules/notification/routines'
import { LSKeys } from './utils/constants'
import RootModals from '@/components/layout/RootModals.vue'
import { toastDefaultOptions } from './config/vue-toast'
import { routeNamesWithoutGA } from './router/routerGuards'
import useSegmentAnalytics from '@/composables/segmentTracking'
import useAnalyticsEvent from '@/composables/useAnalyticsEvent'
import useWhitelabel from '@/composables/useWhitelabel'
import { useRouter } from 'vue-router/composables'
import { emptyGuid } from '@/utils/constants'

export default {
  name: 'App',
  components: {
    RootModals
  },
  setup() {
    const { config } = useWhitelabel('global')
    const { trackIdentity, trackEvent, trackPage } = useSegmentAnalytics()
    const { analyticsGA4Event } = useAnalyticsEvent()
    const router = useRouter()

    router.afterEach((to, from) => {
      if (to.name === from.name) return
      if (!to.meta?.disablePageViewTracking) {
        trackPage('Page Viewed', { page: to.name })
      }
    })

    return { config, trackIdentity, trackEvent, analyticsGA4Event }
  },
  data() {
    return {
      tokenIntervalPeriod: 2000,
      tokenIntervalTimerId: null,
      deployCheckTimerPeriod: 120000,
      deployCheckTimerId: null,
      mobileSidebarOpen: false
    }
  },
  metaInfo() {
    const socialTitle = this.config.clientName || 'Swoop Funding Platform'
    return {
      meta: [
        { property: 'og:title', content: socialTitle },
        { property: 'og:site_name', content: socialTitle },
        { property: 'og:type', content: 'website' },
        { property: 'og:image', content: 'https://swoopfunding.com/wp-content/uploads/2020/08/platform-funding-and-bank-comparison.png' },
        {
          property: 'og:description',
          content: 'Simple and speedy access to business finance. Get matched with tailored funding across loans, equity and grants, and cut business costs in one fell swoop.'
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        { name: 'twitter:title', content: socialTitle },
        {
          name: 'twitter:description',
          content: 'Simple and speedy access to business finance. Get matched with tailored funding across loans, equity and grants, and cut business costs in one fell swoop.'
        },
        { name: 'twitter:image', content: 'https://swoopfunding.com/wp-content/uploads/2020/08/platform-funding-and-bank-comparison.png' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      user: authGetters.USER,
      isLoggedIn: authGetters.IS_AUTHENTICATED,
      tokenExpireDate: authGetters.TOKEN_EXPIRE_DATE,
      notificationConfig: notificationGetters.NOTIFICATION_CONFIG,
      isExternalGroup: authGetters.IS_EXTERNAL_GROUP
    })
  },
  methods: {
    ...mapActions({
      logout: logoutRoutine.TRIGGER
    }),
    toggleMobileSidebar() {
      this.mobileSidebarOpen = !this.mobileSidebarOpen
    },
    closeMobileSidebar() {
      this.mobileSidebarOpen = false
    },
    async checkTokenExpiration() {
      if (this.tokenExpireDate <= new Date()) {
        if (!this.isLoggedIn) return
        await this.logout()
        this.$toasted.show('Session expired. Please Sign in again.', { ...toastDefaultOptions, type: 'warn', icon: 'info' })
        this.trackEvent('Logged Out', {
          reason: 'Session Expired'
        })
      }
    },
    async enableGoogleAnalytics() {
      this.activateGTMInHeader(import.meta.env.VITE_GOOGLE_TAG_ID)
      this.activateGTMInBody(import.meta.env.VITE_GOOGLE_TAG_ID)
    },
    activateGTMInHeader(googleTagId) {
      const scriptId = 'google-analytics-script'

      if (document.getElementById(scriptId) || this.user?.email.endsWith('@swoopfunding.com')) return

      const dataLayer = 'dataLayer'

      window[dataLayer] = window[dataLayer] || []
      window[dataLayer].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

      const f = document.getElementsByTagName('head')[0]
      const j = document.createElement('script')
      const dl = dataLayer !== 'dataLayer' ? '&l=' + dataLayer : ''

      j.id = scriptId
      j.async = true
      j.src = `https://www.googletagmanager.com/gtm.js?id=${googleTagId}${dl}`
      f.insertBefore(j, f.firstChild)
    },
    activateGTMInBody(googleTagId) {
      const noscriptId = 'google-analytics-noscript'

      if (document.getElementById(noscriptId) || this.user?.email.endsWith('@swoopfunding.com')) return

      const body = document.getElementsByTagName('body')[0]
      const noscript = document.createElement('noscript')

      noscript.id = noscriptId
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${googleTagId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`

      body.insertBefore(noscript, body.firstChild)
    },
    refreshWindowTitle(clientName = 'Swoop Funding') {
      const { name, meta } = this.$route
      let title = 'Swoop Funding Platform'

      if (meta?.documentTitle) {
        title = `${meta.documentTitle} - ${clientName}`
      } else if (meta?.sitemap?.title) {
        title = `${meta.sitemap.title} - ${clientName}`
      } else if (name) {
        title =
          name
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ') + ` - ${clientName}`
      }

      document.title = title
    }
  },
  created() {
    if (this.config.tenantId.toLowerCase().replace(/\s/g, '') !== 'comparethemarket') {
      this.enableGoogleAnalytics()
    }
  },
  mounted() {
    if (this.isLoggedIn === true) {
      this.tokenIntervalTimerId = setInterval(this.checkTokenExpiration, this.tokenIntervalPeriod)
    }
  },
  destroyed() {
    clearInterval(this.tokenIntervalTimerId)
    clearTimeout(this.deployCheckTimerId)
  },
  watch: {
    notificationConfig(config) {
      if (Object(config) === config) {
        let message = ''
        if (typeof config.text === 'object' && config.text?.File) {
          message = config.text.File.errors[0].errorMessage || ''
        } else {
          const responseText = config?.text?.split('\n')
          message = responseText[1] && responseText[1].length > 1 && responseText[1]
        }
        message = message && message.match(/Unknown/) ? 'Something went wrong, please try again later' : message
        this.$toasted.clear()
        this.$toasted.show(`${message || config.text}`, { ...(config.options || toastDefaultOptions), type: config.type, icon: config.type, singleton: true })
      }
    },
    isLoggedIn(isLoggedIn) {
      if (isLoggedIn === true) {
        this.tokenIntervalTimerId = setInterval(this.checkTokenExpiration, this.tokenIntervalPeriod)
        const companyId = this.user.companies?.[0]?.companyId
        const isValidCompanyId = companyId && companyId !== emptyGuid

        this.trackIdentity(this.user.userId, {
          name: this.user.firstName + ' ' + this.user.lastName,
          email: this.user.email,
          ...(isValidCompanyId ? { companyId } : {})
        })

        this.trackEvent('Logged In', {
          userId: this.user.userId,
          name: this.user.firstName + ' ' + this.user.lastName,
          email: this.user.email,
          ...(isValidCompanyId ? { companyId } : {})
        })

        this.analyticsGA4Event('SME_login', { url: window.location.href, email: this.user.email })
      } else {
        clearInterval(this.tokenIntervalTimerId)
      }
    },
    $route(to) {
      const isTestUser = this.user?.email.endsWith('@swoopfunding.com')

      // Triggering Google Analytics for every page change.
      if (!routeNamesWithoutGA.includes(to.name) && !isTestUser) {
        this.analyticsGA4Event('pageview')
      }

      this.refreshWindowTitle(this.config.clientName)
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/swoop/_variables.scss';

#swoop-root {
  position: relative;
  overflow-x: clip;

  .root-content {
    display: flex;
    width: 100%;
    min-height: 100vh;
  }

  .main-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background-color: #f7f6f6;

    main.content {
      flex: 1;

      @media only screen and (max-width: $breakpoint-sm-max) {
        width: 100%;
        padding-bottom: 24px;

        &:has(> .application-layout) {
          padding-bottom: 0;
        }
      }
    }
  }
}
</style>

<style scoped>
.loader-screen {
  min-width: 100vw;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
}

.loader {
  opacity: 1;
  display: inline-block;
  animation: spin 800ms ease infinite;
  transition: all 0.2s ease;
}
</style>
